import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

const initialState = {
	list: [],
	team: null,
	ProjectTeam: null,
	roles: [],
	rolesLoading: false,
	loading: false,
	error: null,
};

const extractMessages = (data) => {
	if (Array.isArray(data)) {
		return data;
	}
	if (Array.isArray(data?.messages)) {
		return data.messages;
	}
	if (Array.isArray(data?.result)) {
		return data.result;
	}
	return null;
};

const showSuccessMessages = (data, fallbackMessage) => {
	const messages = extractMessages(data);
	if (messages && messages.length) {
		messages.forEach((msg) => {
			if (msg?.status === "error") {
				toast.error(msg?.message || fallbackMessage?.error || "Request failed");
			} else {
				toast.success(msg?.message || fallbackMessage?.success || "Request successful");
			}
		});
	} else if (fallbackMessage?.success) {
		toast.success(fallbackMessage.success);
	}
};

const showErrorMessages = (errorData, fallbackMessage) => {
	const messages = extractMessages(errorData);
	if (messages && messages.length) {
		messages.forEach((msg) => toast.error(msg?.message || fallbackMessage));
	} else {
		toast.error(errorData?.message || fallbackMessage);
	}
};

const deriveStatus = (data) => {
	const messages = extractMessages(data);
	if (messages && messages.length) {
		const successMessage = messages.find((item) => item?.status === "success");
		const errorMessage = messages.find((item) => item?.status === "error");
		if (successMessage) {
			return {
				status: successMessage.status,
				message: successMessage.message,
				raw: data,
			};
		}
		if (errorMessage) {
			return {
				status: errorMessage.status,
				message: errorMessage.message,
				raw: data,
			};
		}
	}
	if (data?.status || data?.message) {
		return {
			status: data.status ?? "success",
			message: data.message,
			raw: data,
		};
	}
	return { status: "success", message: "success", raw: data };
};

const buildTeamPayload = ({
	ProjectId,
	EmployeeId,
	RoleId = 0,
	AssignedWBSId = 0,
	IsLead = false,
	StartDate = null,
	EndDate = null,
	AllocationPercent = 0,
	Remarks = "",
	DataStatus = 1,
}) => ({
	ProjectId,
	EmployeeId,
	RoleId,
	AssignedWBSId,
	IsLead,
	StartDate,
	EndDate,
	AllocationPercent,
	Remarks,
	DataStatus,
});

const normalizeDate = (value) => {
	if (!value) {
		return null;
	}
	if (value instanceof Date) {
		return value.toISOString().split("T")[0];
	}
	if (typeof value === "string") {
		return value;
	}
	return value?.format ? value.format("YYYY-MM-DD") : null;
};

const normalizeTeamRecordValues = (record = {}, fallbackIndex = 0) => {
	const projectId = Number(record.ProjectId ?? record.projectId ?? 0);
	const employeeId = Number(record.EmployeeId ?? record.employeeId ?? 0);
	const roleId = Number(record.RoleId ?? record.roleId ?? 0);
	const assignedWBSId = Number(record.AssignedWBSId ?? record.assignedWBSId ?? 0);
	const allocationPercent = Number(record.AllocationPercent ?? record.allocationPercent ?? 0);
	const remarks = record.Remarks ?? record.remarks ?? "";
	const isLead = Boolean(record.IsLead ?? record.isLead ?? false);
	const startDate = normalizeDate(record.StartDate ?? record.startDate ?? null);
	const endDate = normalizeDate(record.EndDate ?? record.endDate ?? null);
	const key = record.ProjectTeamId ?? record.projectTeamId ?? `${projectId || "p"}-${employeeId || "e"}-${roleId || "r"}-${assignedWBSId || "w"}-${fallbackIndex}`;
	return {
		projectId,
		employeeId,
		roleId,
		assignedWBSId,
		allocationPercent,
		remarks,
		isLead,
		startDate,
		endDate,
		key,
	};
};

const resolveTeamPayloads = (input) => {
	const projectId = Number(input?.ProjectId ?? input?.projectId ?? 0);
	const roleId = Number(input?.RoleId ?? input?.roleId ?? 0);
	const assignedWBSId = Number(input?.AssignedWBSId ?? input?.assignedWBSId ?? 0);
	const allocationPercent = Number(input?.AllocationPercent ?? input?.allocationPercent ?? 0);
	const remarks = input?.Remarks ?? input?.remarks ?? "";
	const isLead = Boolean(input?.IsLead ?? input?.isLead ?? false);
	const startDate = normalizeDate(input?.StartDate ?? input?.startDate ?? null);
	const endDate = normalizeDate(input?.EndDate ?? input?.endDate ?? null);
	const dataStatus = Number(input?.DataStatus ?? input?.dataStatus ?? 1);

	if (!projectId) {
		return [];
	}

	const members = input?.projectTeamMember;
	if (Array.isArray(members) && members.length) {
		return members.map((employeeId) =>
			buildTeamPayload({
				ProjectId: projectId,
				EmployeeId: Number(employeeId),
				RoleId: roleId,
				AssignedWBSId: assignedWBSId,
				IsLead: isLead,
				StartDate: startDate,
				EndDate: endDate,
				AllocationPercent: allocationPercent,
				Remarks: remarks,
				DataStatus: dataStatus,
			})
		);
	}

	const employeeId = Number(input?.EmployeeId ?? input?.employeeId ?? 0);
	if (!employeeId) {
		return [];
	}

	return [
		buildTeamPayload({
			ProjectId: projectId,
			EmployeeId: employeeId,
			RoleId: roleId,
			AssignedWBSId: assignedWBSId,
			IsLead: isLead,
			StartDate: startDate,
			EndDate: endDate,
			AllocationPercent: allocationPercent,
			Remarks: remarks,
			DataStatus: dataStatus,
		}),
	];
};

export const loadAllProjectTeam = createAsyncThunk(
	"projectTeam/loadAll",
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await axios.get("projects/ProjectTeamDetails");
			return Array.isArray(data) ? data : [];
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load project teams",
				}
			);
		}
	}
);

export const loadProjectRoles = createAsyncThunk(
	"projectTeam/loadRoles",
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await axios.get("roles/RoleDetailsGetbyCompanyId");
			return Array.isArray(data) ? data : [];
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load roles",
				}
			);
		}
	}
);

export const loadAllProjectTeamByProjectId = createAsyncThunk(
	"projectTeam/loadByProject",
	async (projectId, { rejectWithValue }) => {
		try {
			const { data } = await axios.get("projects/ProjectTeamDetails", {
				params: { ProjectId: projectId },
			});
			return Array.isArray(data) ? data : [];
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load project team",
				}
			);
		}
	}
);

export const loadProjectTeamDetailsByProjectId = createAsyncThunk(
	"projectTeam/loadDetailsByProjectId",
	async (projectId, { rejectWithValue }) => {
		try {
			const { data } = await axios.get(`projects/ProjectTeamDetailsByProjectId`, {
				params: { ProjectId: projectId },
			});
			return Array.isArray(data) ? data : [];
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load project team details",
				}
			);
		}
	}
);

export const loadSingleProjectTeam = createAsyncThunk(
	"projectTeam/loadSingle",
	async (projectTeamId, { rejectWithValue }) => {
		try {
			const { data } = await axios.get("projects/ProjectTeamDetails", {
				params: { ProjectTeamId: projectTeamId },
			});
			if (Array.isArray(data)) {
				return data[0] ?? null;
			}
			return data;
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load project team",
				}
			);
		}
	}
);

export const addSingleProjectTeam = createAsyncThunk(
	"projectTeam/save",
	async (payload, { rejectWithValue }) => {
		const payloads = resolveTeamPayloads(payload ?? {});
		if (!payloads.length) {
			toast.error("Missing required team assignment details");
			return rejectWithValue({ message: "Invalid team assignment payload" });
		}
		try {
			const responses = await Promise.all(
				payloads.map((body) => axios.post("projects/ProjectTeamDetailsSetup", body))
			);
			const aggregated = responses.flatMap((response) => {
				const responseData = response?.data;
				if (Array.isArray(responseData)) {
					return responseData;
				}
				return responseData ? [responseData] : [];
			});
			showSuccessMessages(aggregated, {
				success: payloads.length > 1 ? "Team members saved" : "Team member saved",
			});
			return deriveStatus(aggregated?.[0] ?? { status: "success", message: "success" });
		} catch (error) {
			const errorData = error?.response?.data;
			showErrorMessages(errorData, "Failed to save project team");
			return rejectWithValue(
				errorData ?? {
					message: error?.message || "Failed to save project team",
				}
			);
		}
	}
);

export const updateProjectTeam = createAsyncThunk(
	"projectTeam/update",
	async ({ id, values }, { rejectWithValue }) => {
		const merged = {
			...(values ?? {}),
			ProjectId: values?.ProjectId ?? values?.projectId ?? id,
		};
		const payloads = resolveTeamPayloads(merged);
		if (!payloads.length) {
			toast.error("No team members selected");
			return rejectWithValue({ message: "No team members" });
		}
		try {
			const responses = await Promise.all(
				payloads.map((body) => axios.post("projects/ProjectTeamDetailsSetup", body))
			);
			const aggregated = responses.flatMap((response) => {
				const responseData = response?.data;
				if (Array.isArray(responseData)) {
					return responseData;
				}
				return responseData ? [responseData] : [];
			});
			showSuccessMessages(aggregated, { success: "Team updated" });
			return deriveStatus(aggregated?.[0] ?? { status: "success", message: "success" });
		} catch (error) {
			const errorData = error?.response?.data;
			showErrorMessages(errorData, "Failed to update project team");
			return rejectWithValue(
				errorData ?? {
					message: error?.message || "Failed to update project team",
				}
			);
		}
	}
);

export const deleteProjectTeam = createAsyncThunk(
	"projectTeam/delete",
	async (payload, { getState, rejectWithValue }) => {
		const isObjectPayload = typeof payload === "object" && payload !== null;
		const identifier = isObjectPayload ? payload : { ProjectTeamId: payload };
		const identifierKey = identifier.ProjectTeamId ?? identifier.projectTeamId ?? null;
		const identifierValues = normalizeTeamRecordValues(identifier);
		const state = getState();
		const candidateList = Array.isArray(state.projectTeam?.list) ? state.projectTeam.list : [];
		const relatedRecords = candidateList.filter((item) => {
			const itemValues = normalizeTeamRecordValues(item);
			const itemKey = itemValues.key ?? null;
			if (identifierKey !== null && itemKey !== null) {
				return String(itemKey) === String(identifierKey);
			}
			if (identifierValues.projectId && identifierValues.employeeId) {
				return (
					itemValues.projectId === identifierValues.projectId &&
					itemValues.employeeId === identifierValues.employeeId &&
					itemValues.roleId === identifierValues.roleId &&
					itemValues.assignedWBSId === identifierValues.assignedWBSId
				);
			}
			return false;
		});

		const sources = relatedRecords.length ? relatedRecords : [identifier];
		const payloads = sources
			.map((record, index) => {
				const values = normalizeTeamRecordValues(record, index);
				if (!values.projectId || !values.employeeId) {
					return null;
				}
				return buildTeamPayload({
					ProjectId: values.projectId,
					EmployeeId: values.employeeId,
					RoleId: values.roleId,
					AssignedWBSId: values.assignedWBSId,
					IsLead: values.isLead,
					StartDate: values.startDate,
					EndDate: values.endDate,
					AllocationPercent: values.allocationPercent,
					Remarks: values.remarks,
					DataStatus: 2,
				});
			})
			.filter(Boolean);

		if (!payloads.length) {
			toast.error("Unable to resolve team member for deletion");
			return rejectWithValue({ message: "Missing team member details" });
		}

		try {
			const responses = await Promise.all(
				payloads.map((body) => axios.post("projects/ProjectTeamDetailsSetup", body))
			);
			const aggregated = responses.flatMap((response) => {
				const responseData = response?.data;
				if (Array.isArray(responseData)) {
					return responseData;
				}
				return responseData ? [responseData] : [];
			});
			showSuccessMessages(aggregated, { success: "Team member deleted" });
			return deriveStatus(aggregated?.[0] ?? { status: "success", message: "success" });
		} catch (error) {
			const errorData = error?.response?.data;
			showErrorMessages(errorData, "Failed to delete project team");
			return rejectWithValue(
				errorData ?? {
					message: error?.message || "Failed to delete project team",
				}
			);
		}
	}
);

export const updateProjectTeamStatus = createAsyncThunk(
	"projectTeam/updateStatus",
	async ({ id, values }, { getState, rejectWithValue }) => {
		const state = getState();
		const desiredStatus = typeof values === "object" ? values?.status : values;
		const dataStatus = desiredStatus === false ? 0 : 1;
		const candidateList = Array.isArray(state.projectTeam?.list) ? state.projectTeam.list : [];
		const identifierKey = id;
		const relatedRecords = candidateList.filter((item) => {
			const itemValues = normalizeTeamRecordValues(item);
			const itemKey = itemValues.key ?? null;
			if (itemKey !== null && identifierKey !== undefined && identifierKey !== null) {
				return String(itemKey) === String(identifierKey);
			}
			return false;
		});
		if (!relatedRecords.length) {
			toast.error("Unable to locate team members to update status");
			return rejectWithValue({ message: "No team members found" });
		}
		const payloads = relatedRecords
			.map((record, index) => {
				const values = normalizeTeamRecordValues(record, index);
				if (!values.projectId || !values.employeeId) {
					return null;
				}
				return buildTeamPayload({
					ProjectId: values.projectId,
					EmployeeId: values.employeeId,
					RoleId: values.roleId,
					AssignedWBSId: values.assignedWBSId,
					IsLead: values.isLead,
					StartDate: values.startDate,
					EndDate: values.endDate,
					AllocationPercent: values.allocationPercent,
					Remarks: values.remarks,
					DataStatus: dataStatus,
				});
			})
			.filter(Boolean);

		if (!payloads.length) {
			toast.error("Unable to build status update payload");
			return rejectWithValue({ message: "Invalid payload" });
		}

		try {
			const responses = await Promise.all(
				payloads.map((body) => axios.post("projects/ProjectTeamDetailsSetup", body))
			);
			const aggregated = responses.flatMap((response) => {
				const responseData = response?.data;
				if (Array.isArray(responseData)) {
					return responseData;
				}
				return responseData ? [responseData] : [];
			});
			showSuccessMessages(aggregated, { success: "Team status updated" });
			return deriveStatus(aggregated?.[0] ?? { status: "success", message: "success" });
		} catch (error) {
			const errorData = error?.response?.data;
			showErrorMessages(errorData, "Failed to update team status");
			return rejectWithValue(
				errorData ?? {
					message: error?.message || "Failed to update team status",
				}
			);
		}
	}
);

const projectTeamSlice = createSlice({
	name: "projectTeam",
	initialState,
	reducers: {
		clearProjectTeam: (state) => {
			state.list = [];
			state.team = null;
			state.ProjectTeam = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(loadProjectRoles.pending, (state) => {
				state.rolesLoading = true;
			})
			.addCase(loadProjectRoles.fulfilled, (state, action) => {
				state.rolesLoading = false;
				state.roles = Array.isArray(action.payload) ? action.payload : [];
			})
			.addCase(loadProjectRoles.rejected, (state, action) => {
				state.rolesLoading = false;
				state.error = action.payload?.message || "Unable to load roles";
			})
			.addCase(loadAllProjectTeam.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(loadAllProjectTeam.fulfilled, (state, action) => {
				state.loading = false;
				state.list = Array.isArray(action.payload) ? action.payload : [];
			})
			.addCase(loadAllProjectTeam.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to load project teams";
			})
			.addCase(loadAllProjectTeamByProjectId.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(loadAllProjectTeamByProjectId.fulfilled, (state, action) => {
				state.loading = false;
				state.list = Array.isArray(action.payload) ? action.payload : [];
			})
			.addCase(loadAllProjectTeamByProjectId.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to load project team";
			})
			.addCase(loadProjectTeamDetailsByProjectId.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(loadProjectTeamDetailsByProjectId.fulfilled, (state, action) => {
				state.loading = false;
				state.list = Array.isArray(action.payload) ? action.payload : [];
			})
			.addCase(loadProjectTeamDetailsByProjectId.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to load project team details";
			})
			.addCase(loadSingleProjectTeam.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(loadSingleProjectTeam.fulfilled, (state, action) => {
				state.loading = false;
				state.team = action.payload ?? null;
				state.ProjectTeam = action.payload ?? null;
			})
			.addCase(loadSingleProjectTeam.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to load project team";
			})
			.addCase(addSingleProjectTeam.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(addSingleProjectTeam.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(addSingleProjectTeam.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to save project team";
			})
			.addCase(updateProjectTeam.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateProjectTeam.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(updateProjectTeam.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to update project team";
			})
			.addCase(deleteProjectTeam.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(deleteProjectTeam.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(deleteProjectTeam.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to delete project team";
			})
			.addCase(updateProjectTeamStatus.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateProjectTeamStatus.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(updateProjectTeamStatus.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to update team status";
			});
	},
});

export default projectTeamSlice.reducer;
export const { clearProjectTeam } = projectTeamSlice.actions;
